import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { api } from "src/api";
import {
  ReferralPayout,
  ReferralPayoutStatusEnum,
} from "src/api/generated/types";
import { Button } from "src/components";
import { useClientById, useMyToast } from "src/hooks";
import { useIsLargeScreen } from "src/hooks/useScreenSize";

type ReferralPayoutsResponse = {
  getAllReferralsPayouts: {
    total: number;
    payouts: any[];
  };
};

function ReferralPayouts() {
  const { show: showToast } = useMyToast();
  const { data, refetch } = useQuery<ReferralPayoutsResponse>(
    api.referrals.getAllReferralPayouts,
    {
      variables: {
        status: ReferralPayoutStatusEnum.Pending,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const [markAsPaid] = useMutation(api.referrals.markAsPaid);

  const handleMarkAsPaid = async (payoutId: string) => {
    try {
      await markAsPaid({
        variables: { payoutId },
      });
      showToast({
        message: "Referral payout marked as paid",
        status: "success",
      });
      refetch();
    } catch (error) {
      showToast({
        message: "Failed to mark payout as paid",
        status: "error",
      });
    }
  };

  const getStatusColor = (status: ReferralPayoutStatusEnum) => {
    switch (status) {
      case ReferralPayoutStatusEnum.Pending:
        return "yellow";
      case ReferralPayoutStatusEnum.Succeeded:
        return "green";
      case ReferralPayoutStatusEnum.Cancelled:
        return "gray";
      default:
        return "gray";
    }
  };

  const payouts = data?.getAllReferralsPayouts.payouts ?? [];
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Container maxW="900px" py={8}>
      <Heading size="lg" mb={8} textAlign="center">
        Referral Payout Requests
      </Heading>

      <VStack spacing={4} align="stretch" maxW="800px" mx="auto">
        {payouts.map((payout: ReferralPayout) => (
          <Box
            key={payout.id}
            bg={cardBg}
            borderRadius="xl"
            p={6}
            borderWidth="1px"
            borderColor={borderColor}
            transition="all 0.2s"
          >
            <Flex justify="space-between" align="start" gap={6}>
              <Box flex="1">
                <Flex align="center" gap={3} mb={3}>
                  <Text fontSize="xl" fontWeight="bold">
                    {payout.payoutClient?.name || "Unnamed Client"}
                  </Text>
                  <Badge
                    colorScheme={getStatusColor(payout.status)}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                  >
                    {payout.status}
                  </Badge>
                </Flex>

                <VStack align="start" spacing={2} color="gray.600">
                  {payout.payoutClient?.paypalUsername && (
                    <Flex align="center" gap={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        PayPal:
                      </Text>
                      <Text fontSize="sm">
                        {payout.payoutClient.paypalUsername}
                      </Text>
                    </Flex>
                  )}
                  {payout.payoutClient?.solReferralAddress && (
                    <Flex align="center" gap={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        Solana:
                      </Text>
                      <Text fontSize="sm" fontFamily="mono">
                        {payout.payoutClient.solReferralAddress}
                      </Text>
                    </Flex>
                  )}
                  <br />
                  <Text fontSize="sm" mt={2}>
                    Requested: {new Date(payout.createdAt).toLocaleDateString()}
                  </Text>

                  <Text fontSize="sm" mt={2}>
                    Paid:{" "}
                    {payout.paidAt
                      ? new Date(payout.paidAt).toLocaleDateString()
                      : "Not paid yet"}
                  </Text>
                </VStack>
              </Box>

              <VStack align="end" spacing={4}>
                <Text fontSize="2xl" fontWeight="bold">
                  ${(payout.amountCents / 100).toFixed(2)}
                </Text>
                {payout.status === ReferralPayoutStatusEnum.Pending && (
                  <Button
                    size="md"
                    variant="primary"
                    onClick={() => handleMarkAsPaid(payout.id)}
                    px={6}
                  >
                    Mark as Paid
                  </Button>
                )}
              </VStack>
            </Flex>
          </Box>
        ))}
      </VStack>
    </Container>
  );
}

function AllReferrals() {
  const params = useParams<{ clientId: string }>();
  const clientId = params.clientId;
  const [updateClient] = useMutation(api.clients.update);
  const { client } = useClientById(clientId || "", {
    onlyFetchClient: true,
  });

  const isLarge = useIsLargeScreen();
  const { show: showToast } = useMyToast();

  const { data: referralsData } = useQuery<ReferralPayoutsResponse>(
    api.referrals.getAllReferralPayouts
  );
  const [markAsPaid] = useMutation(api.referrals.markAsPaid);

  return (
    <Box p={4}>
      <ReferralPayouts />
    </Box>
  );
}

export default AllReferrals;
